// Google Static Maps URL builder for a delivery run. Extracted from the
// /deliveries route so the runs rail (M1) and driver view (M3) reuse one
// implementation. Pure — no React (the API key is passed in by the caller,
// which reads it from sys-config).
//
// Static Maps caps the request URL (~8KB) and gets unwieldy with many markers,
// so bound both: at most MAX_STATIC_MARKERS markers, and if the assembled URL
// still exceeds MAX_STATIC_MAP_URL, drop the preview ('') rather than emit a URL
// the browser silently 404s on. The "Open in Google Maps" deep link (BE-
// truncated to 9 waypoints) stays the primary navigation path.

import type { DeliveryRun } from '@/api/deliveryRuns'
import { DELIVERY_STATUS } from '@/api/receipts'

export const MAX_STATIC_MARKERS = 25
export const MAX_STATIC_MAP_URL = 8000

// Marker colour by stop status so the map reads progress at a glance:
// delivered = green, out for delivery (in transit) = blue, failed = red,
// everything still pending = gray.
function markerColor(status: string): string {
  switch (status) {
    case DELIVERY_STATUS.DELIVERED:
      return 'green'
    case DELIVERY_STATUS.OUT_FOR_DELIVERY:
      return 'blue'
    case DELIVERY_STATUS.DELIVERY_FAILED:
      return 'red'
    default:
      return 'gray'
  }
}

// Build a Google Static Maps URL from the run's polyline + stop markers.
// Returns '' when there's no key, nothing to draw, or the URL is over-length.
export function buildStaticMapUrl(run: DeliveryRun, key: string): string {
  if (!key) return ''
  const stops = run.stops ?? []
  const markers = stops
    .filter((s) => s.latitude && s.longitude)
    .slice(0, MAX_STATIC_MARKERS)
    .map(
      (s, i) =>
        `markers=color:${markerColor(s.delivery_status)}|label:${i + 1}|${s.latitude},${s.longitude}`,
    )
  const parts = ['size=640x320', 'maptype=roadmap', ...markers]
  if (run.route_polyline) {
    parts.push(`path=weight:3|color:0x1d4ed8ff|enc:${run.route_polyline}`)
  }
  if (markers.length === 0 && !run.route_polyline) return ''
  parts.push(`key=${key}`)
  const url = `https://maps.googleapis.com/maps/api/staticmap?${parts.join('&')}`
  // A long polyline (many stops) can blow the limit even within the marker
  // cap; drop the preview rather than ship a broken image.
  return url.length > MAX_STATIC_MAP_URL ? '' : url
}
